import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

/**
 * InstitutionalDataContext
 *
 * Shared, in-memory, stale-while-revalidate cache for the data that powers
 * the institutional admin dashboard, user/program management page, evaluations
 * pages, and related views. Navigating between these pages previously
 * re-issued the same fetches from scratch; this context lets the UI render
 * from cached data instantly while a background refresh keeps it fresh.
 */

const InstitutionalDataContext = createContext(null);

const STALE_MS = 30 * 1000; // data is considered fresh for 30s
const MAX_AGE_MS = 5 * 60 * 1000; // forcibly re-fetch after 5 min

const RESOURCES = {
  institution: { url: `${API_BASE_URL}/api/institutional-admin/institution` },
  programs: { url: `${API_BASE_URL}/api/institutional-admin/programs` },
  users: { url: `${API_BASE_URL}/api/institutional-admin/users` },
  analytics: { url: `${API_BASE_URL}/api/institutional-admin/analytics` },
  evaluationTemplates: { url: `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-templates` },
  evaluationResults: { url: `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-results` },
};

const makeInitialState = () =>
  Object.keys(RESOURCES).reduce((acc, key) => {
    acc[key] = { data: null, loading: false, error: null, fetchedAt: 0 };
    return acc;
  }, {});

export function useInstitutionalData() {
  const ctx = useContext(InstitutionalDataContext);
  if (!ctx) {
    throw new Error('useInstitutionalData must be used within InstitutionalDataProvider');
  }
  return ctx;
}

export const InstitutionalDataProvider = ({ children }) => {
  const [state, setState] = useState(makeInitialState);
  // Mirror of state for synchronous reads inside fetchResource — avoids races
  // from reading the closure-captured `state` variable.
  const stateRef = useRef(state);
  stateRef.current = state;
  // Tracks in-flight requests per resource so concurrent callers share a single fetch.
  const inflightRef = useRef({});

  const setResource = useCallback((key, patch) => {
    setState((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  }, []);

  const fetchResource = useCallback(
    (key, { force = false } = {}) => {
      const resource = RESOURCES[key];
      if (!resource) return Promise.resolve(null);

      const token = localStorage.getItem('token');
      if (!token) return Promise.resolve(null);

      const existing = inflightRef.current[key];
      if (existing) return existing;

      const entry = stateRef.current[key] || { fetchedAt: 0 };
      const age = Date.now() - (entry.fetchedAt || 0);
      const needsFetch = force || !entry.data || age > STALE_MS;
      if (!needsFetch) {
        return Promise.resolve(entry.data);
      }

      setResource(key, { loading: true, error: null });

      const promise = (async () => {
        try {
          const res = await fetch(resource.url, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || `Failed to load ${key}`);
          }
          const data = await res.json();
          setResource(key, {
            data,
            loading: false,
            error: null,
            fetchedAt: Date.now(),
          });
          return data;
        } catch (err) {
          setResource(key, { loading: false, error: err.message || 'Request failed' });
          throw err;
        } finally {
          delete inflightRef.current[key];
        }
      })();

      inflightRef.current[key] = promise;
      return promise;
    },
    [setResource]
  );

  const ensureResource = useCallback(
    (key) => {
      const entry = stateRef.current[key];
      const age = Date.now() - (entry?.fetchedAt || 0);
      if (!entry?.data || age > MAX_AGE_MS) {
        return fetchResource(key);
      }
      if (age > STALE_MS && !entry.loading) {
        // Stale-while-revalidate: serve cached data immediately and refresh in bg.
        fetchResource(key).catch(() => {});
      }
      return entry.data;
    },
    [fetchResource]
  );

  const invalidate = useCallback(
    (keys, { refetch = true } = {}) => {
      const list = Array.isArray(keys) ? keys : [keys];
      setState((prev) => {
        const next = { ...prev };
        for (const k of list) {
          if (next[k]) {
            next[k] = { ...next[k], fetchedAt: 0 };
          }
        }
        return next;
      });
      if (refetch) {
        for (const k of list) {
          if (RESOURCES[k]) fetchResource(k, { force: true }).catch(() => {});
        }
      }
    },
    [fetchResource]
  );

  const invalidateAll = useCallback(() => {
    invalidate(Object.keys(RESOURCES));
  }, [invalidate]);

  // Token change (login/logout) invalidates all institutional caches.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') invalidateAll();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [invalidateAll]);

  const value = useMemo(
    () => ({
      institution: state.institution,
      programs: state.programs,
      users: state.users,
      analytics: state.analytics,
      evaluationTemplates: state.evaluationTemplates,
      evaluationResults: state.evaluationResults,
      fetchResource,
      ensureResource,
      invalidate,
      invalidateAll,
    }),
    [state, fetchResource, ensureResource, invalidate, invalidateAll]
  );

  return (
    <InstitutionalDataContext.Provider value={value}>
      {children}
    </InstitutionalDataContext.Provider>
  );
};

/**
 * Convenience hook: load one or more institutional resources and return their
 * states. Fetches on mount and reuses cached data across pages.
 */
export function useInstitutionalResources(keys) {
  const ctx = useInstitutionalData();
  const list = useMemo(() => (Array.isArray(keys) ? keys : [keys]), [keys]);
  const keyStr = list.join(',');
  const { ensureResource, fetchResource } = ctx;

  useEffect(() => {
    for (const key of list) {
      ensureResource(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyStr]);

  const entries = list.reduce((acc, key) => {
    acc[key] = ctx[key];
    return acc;
  }, {});

  const refresh = useCallback(
    () => Promise.all(list.map((k) => fetchResource(k, { force: true }).catch(() => null))),
    [list, fetchResource]
  );

  const loading = list.some((k) => ctx[k]?.loading && !ctx[k]?.data);
  const anyLoading = list.some((k) => ctx[k]?.loading);
  const error = list.map((k) => ctx[k]?.error).find(Boolean) || null;

  return { entries, loading, anyLoading, error, refresh };
}
